import React, {
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef
} from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { Box, Icon, ThemeProvider, Text } from '@front10/base-elements';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import { Listbox } from '../../lab/Listbox';
import { useIsMobileView } from '@front10/utils';

const CurrencyFilter = (props, ref) => {
  const {
    value,
    currencies,
    disabled,
    isMobile = false,
    onChange,
    filterButtonIconProps,
    menuContainer = {
      'cmp-styles': {
        width: '100%'
      }
    },
    itemContainer = {
      'cmp-styles': {
        height: '50px',
        'line-height': '50px'
      }
    },
    selectedItem = {
      'font-weight': 'bold',
      'padding-right': '10px'
    },
    item = {
      borderColor: 'borders',
      'font-weight': 'normal',
      'padding-right': '10px'
    },
    iconCheckProps = {}
  } = props;

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const options = currencies.data;
  const title = translate('cmp-currency-unit', 'All currencies');

  const { currencyOptions, optionAllCurrencies } = useMemo(() => {
    const optionAllCurrencies = {
      value: 'ALL_CURRENCIES',
      label: translate('cmp-all-currencies', 'All currencies')
    };
    const currencyOptions = (Array.isArray(options) ? options : []).map((option) => ({
      ...option,
      value: option.currencyCode,
      label: option.currencyCode
    }));

    return { currencyOptions: [optionAllCurrencies, ...currencyOptions], optionAllCurrencies };
  }, [options, translate]);

  const [currencyFilter, setCurrencyFilter] = useState(value);
  const flexibleRef = useRef();

  useEffect(() => {
    setCurrencyFilter(value);
  }, [value]);

  let mobileView = useIsMobileView();
  if (isMobile) {
    mobileView = true;
  }

  const listboxRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        listboxRef.current.focusToggleElement();
      }
    }),
    []
  );

  const desktopLayout = (
    <Listbox
      ref={listboxRef}
      label={title}
      options={currencyOptions}
      defaultValue={value}
      forcePlaceholder={disabled}
      focusWhenIsShow={false}
      disabled={disabled}
      onChange={(value) => onChange({ value })}
      filterButtonIconProps={filterButtonIconProps}
    />
  );

  const mobileLayout = (
    <FlexibleFilterContainer
      ref={flexibleRef}
      showSeparator
      saveOnClose={false}
      onBackButtonPress={() => setCurrencyFilter(value)}
      onSave={() => {
        onChange({ value: currencyFilter });
      }}
      variant="layout.popover"
      label={title}
      title={title}
      displayFooter={true}
      toggleElement={
        <FilterButton
          iconSettings={filterButtonIconProps}
          value={
            value && !disabled
              ? `${
                  currencyFilter === 'ALL_CURRENCIES' ? optionAllCurrencies.label : currencyFilter
                }`
              : title
          }
          aria-label={`${title} ${
            value
              ? `${
                  currencyFilter === 'ALL_CURRENCIES' ? optionAllCurrencies.label : currencyFilter
                }`
              : ''
          }`}
          active={Boolean(value)}
          disabled={disabled} // used to disabled the component
        />
      }
    >
      <Panels showSeparator={false}>
        <Box {...menuContainer}>
          {currencyOptions.map((option) => (
            <Box {...itemContainer}>
              <Box
                onClick={() => setCurrencyFilter(option.value)}
                className="cmp-list-selector-item"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  variant: 'button.base',
                  padding: '15px 10px',
                  borderBottom: true,
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: item.borderColor
                }}
              >
                <Text
                  sx={{}}
                  variant="text.fontSize5"
                  style={currencyFilter === option.value ? selectedItem : item}
                >
                  {option.label}
                </Text>
                {currencyFilter === option.value && (
                  <Icon
                    className="fr-rotate"
                    icon="check"
                    size={20}
                    sx={{
                      stroke: 'primary',
                      verticalAlign: 'middle',
                      strokeWidth: '1px'
                    }}
                    {...iconCheckProps}
                  ></Icon>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Panels>
    </FlexibleFilterContainer>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        {!mobileView ? desktopLayout : mobileLayout}
      </ThemeProvider>
    </EmotionCache>
  );
};

export default forwardRef(CurrencyFilter);
